import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { SubscriptionPlan } from "./contracts/SubscriptionPlan";
import { SubscriptionTier } from "./contracts/SubscriptionTier";
import {
  startSubscriptionCheckout,
  syncCheckout,
} from "./slice-extensions/subscription-slice-extension";
import { initPaddle, openPaddleCheckout, PADDLE_NOT_CONFIGURED } from "./paddle";
import { CHECKOUT_RETURN_PARAMS, parseCheckoutReturn } from "./utils/checkoutReturn";

/**
 * Confirm a completed checkout a few times: the provider can take a moment to
 * read the payment back as paid right after capture. The backend sync is
 * idempotent (no-op once active).
 */
const SYNC_ATTEMPTS = 3;
const SYNC_INTERVAL_MS = 3000;

/**
 * Drives the subscription checkout for both providers. Paddle checks out in the
 * Paddle.js overlay and completes via its event callback; Stripe redirects to
 * the hosted checkout page and returns via ?checkout=…&session_id=… URL params.
 * Either way the completed checkout is confirmed immediately through the sync
 * endpoint, without waiting for the provider webhook.
 */
export const useSubscriptionCheckout = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  // Consume the return params exactly once — effects re-run (StrictMode,
  // re-renders before the URL is cleaned) and must not repeat the toast.
  const consumedReturn = useRef(false);

  const syncRepeatedly = useCallback(
    (checkoutId: string) => {
      let attempts = 0;
      const sync = () => {
        dispatch(syncCheckout(checkoutId));
        attempts += 1;
        if (attempts < SYNC_ATTEMPTS) {
          window.setTimeout(sync, SYNC_INTERVAL_MS);
        }
      };
      sync();
    },
    [dispatch]
  );

  // Boot Paddle.js once; it completes checkout in-page via this callback.
  useEffect(() => {
    initPaddle((transactionId) => {
      toast.success(t("subscription.page.checkoutComplete"));
      syncRepeatedly(transactionId);
    });
  }, [syncRepeatedly, t]);

  // Stripe's hosted checkout returns by redirecting back with URL params —
  // consume them once, then strip them so a refresh doesn't repeat the toast.
  useEffect(() => {
    const returned = parseCheckoutReturn(searchParams);
    if (!returned || consumedReturn.current) {
      return;
    }
    consumedReturn.current = true;

    if (returned.status === "success") {
      toast.success(t("subscription.page.checkoutComplete"));
      syncRepeatedly(returned.sessionId);
    } else {
      toast.info(t("subscription.page.checkoutCancelled"));
    }

    const cleaned = new URLSearchParams(searchParams);
    CHECKOUT_RETURN_PARAMS.forEach((param) => cleaned.delete(param));
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams, syncRepeatedly, t]);

  const handleSubscribe = async (plan: SubscriptionPlan, tier: SubscriptionTier) => {
    try {
      const checkout = await dispatch(startSubscriptionCheckout({ plan, tier })).unwrap();
      if (checkout.checkoutUrl) {
        // Stripe: hand the browser over to the hosted checkout page.
        window.location.assign(checkout.checkoutUrl);
        return;
      }
      await openPaddleCheckout(checkout);
    } catch (error) {
      const message = (error as { message?: string })?.message;
      if (message === PADDLE_NOT_CONFIGURED) {
        toast.error(t("subscription.page.checkoutNotConfigured"));
        return;
      }
      toast.error(message || t("subscription.page.checkoutFailed"));
    }
  };

  return { handleSubscribe };
};
